import { useState, useRef, useEffect, useCallback } from 'react'
import { X, Copy, Check, Link2, Eye, Edit3, Lock, AlertTriangle, FileText, Smartphone, Loader2 } from 'lucide-react'
import QRCode from 'qrcode'
import { useSharing, useWorkspaceMetadata } from '../hooks/useWorkspace'
import './ShareModal.css'

type ShareMode = 'read' | 'readwrite'

interface ShareModalProps {
    isOpen: boolean
    onClose: () => void
    currentFilePath?: string | null
    currentViewMode?: 'edit' | 'preview' | null
}

export default function ShareModal({ isOpen, onClose, currentFilePath, currentViewMode }: ShareModalProps) {
    const { createInviteLink } = useSharing()
    const { canWrite } = useWorkspaceMetadata()
    const [mode, setMode] = useState<ShareMode>('read')
    const [generatedLink, setGeneratedLink] = useState('')
    const [copied, setCopied] = useState(false)
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
    const [inviteError, setInviteError] = useState<string | null>(null)
    const [creating, setCreating] = useState(false)

    const linkRef = useRef<HTMLInputElement>(null)
    const inviteRequestIdRef = useRef(0)

    useEffect(() => {
        if (isOpen) {
            setCopied(false)
        }
    }, [isOpen])

    useEffect(() => {
        if (!canWrite && mode === 'readwrite') {
            setMode('read')
        }
    }, [canWrite, mode])

    // Async invite creation — every call gets a fresh request id so stale
    // responses from old auto/manual requests are discarded consistently.
    const generateInvite = useCallback(async (shareMode: ShareMode) => {
        const requestId = ++inviteRequestIdRef.current
        const viewMode = currentFilePath ? (currentViewMode ?? undefined) : undefined
        try {
            setCreating(true)
            setInviteError(null)
            const result = await createInviteLink(shareMode, currentFilePath ?? undefined, viewMode)
            if (inviteRequestIdRef.current !== requestId) return
            setGeneratedLink(result.url)
            setCopied(false)
        } catch (err) {
            if (inviteRequestIdRef.current !== requestId) return
            setInviteError(err instanceof Error ? err.message : 'Failed to create invite')
            setGeneratedLink('')
        } finally {
            if (inviteRequestIdRef.current === requestId) {
                setCreating(false)
            }
        }
    }, [createInviteLink, currentFilePath, currentViewMode])

    // Auto-generate invite on open/mode change; cleanup invalidates any older
    // request so mode changes and close/reopen cycles cannot apply stale data.
    useEffect(() => {
        if (!isOpen) {
            inviteRequestIdRef.current += 1
            return
        }

        void generateInvite(mode)

        return () => {
            inviteRequestIdRef.current += 1
        }
    }, [isOpen, mode, generateInvite])

    // Generate QR code when link changes
    useEffect(() => {
        if (!generatedLink) { setQrDataUrl(null); return }

        let cancelled = false

        // Resolve CSS accent color to hex — qrcode library needs #rrggbb,
        // but --color-accent is typically HSL. Use a temp element to let the
        // browser compute the final rgb() value, then convert.
        let accentHex = '#4f6ef7'
        try {
            const probe = document.createElement('div')
            probe.style.color = 'var(--color-accent)'
            document.body.appendChild(probe)
            const rgb = getComputedStyle(probe).color // "rgb(r, g, b)"
            document.body.removeChild(probe)
            const m = rgb.match(/(\d+)/g)
            if (m && m.length >= 3) {
                accentHex = '#' + m.slice(0, 3).map(c => (+c).toString(16).padStart(2, '0')).join('')
            }
        } catch { /* fallback to default */ }

        QRCode.toDataURL(generatedLink, {
            width: 200,
            margin: 2,
            color: { dark: accentHex, light: '#00000000' },
            errorCorrectionLevel: 'M',
        }).then(url => { if (!cancelled) setQrDataUrl(url) })
            .catch(() => { if (!cancelled) setQrDataUrl(null) })

        return () => { cancelled = true }
    }, [generatedLink])

    function handleCopy() {
        navigator.clipboard.writeText(generatedLink).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2500)
        })
    }

    if (!isOpen) return null

    return (
        <>
            <div className="share-backdrop" onClick={onClose} />
            <div className="share-modal">
                <div className="share-header">
                    <h2>Share workspace</h2>
                    <button className="btn btn-icon" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="share-content">
                    {/* Workspace scope notice */}
                    <div className="share-scope-notice">
                        <AlertTriangle size={15} />
                        <span>This link shares the <strong>entire workspace</strong>, not just one file.</span>
                    </div>

                    {/* File context hint */}
                    {currentFilePath && (
                        <div className="share-file-hint">
                            <FileText size={14} />
                            <span>Opens to: <strong>{currentFilePath.split('/').pop()}</strong></span>
                        </div>
                    )}

                    {/* Mode selector */}
                    <div className="share-mode-selector">
                        <button
                            className={`share-mode-btn ${mode === 'read' ? 'active' : ''}`}
                            onClick={() => setMode('read')}
                        >
                            <Eye size={18} />
                            <div>
                                <strong>Read access</strong>
                                <span>Can view files, not edit</span>
                            </div>
                        </button>
                        {canWrite && (
                            <button
                                className={`share-mode-btn ${mode === 'readwrite' ? 'active' : ''}`}
                                onClick={() => setMode('readwrite')}
                            >
                                <Edit3 size={18} />
                                <div>
                                    <strong>Read + Write</strong>
                                    <span>Can edit and view all files</span>
                                </div>
                            </button>
                        )}
                    </div>

                    {/* Security note */}
                    <div className={`share-security-note ${mode === 'readwrite' ? 'warning' : ''}`}>
                        <Lock size={16} />
                        <span>
                            {mode === 'read'
                                ? 'This link grants encrypted read access. The recipient cannot modify content.'
                                : 'This link grants full write access. Only share with people you trust!'
                            }
                        </span>
                    </div>

                    {/* Generated invite link */}
                    <div className="share-link-container">
                        <div className="share-link-label">
                            <Link2 size={14} />
                            <span>{mode === 'read' ? 'Read-only link' : 'Read+Write link'}</span>
                        </div>
                        {creating ? (
                            <div className="share-link-row" style={{ justifyContent: 'center', padding: '12px' }}>
                                <Loader2 size={18} className="spin" />
                                <span>Creating invite…</span>
                            </div>
                        ) : inviteError ? (
                            <div className="share-link-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                                <span style={{ color: 'var(--color-error, #e53e3e)', fontSize: '0.85em' }}>{inviteError}</span>
                                <button className="btn" onClick={() => void generateInvite(mode)}>Retry</button>
                            </div>
                        ) : (
                            <div className="share-link-row">
                                <input
                                    ref={linkRef}
                                    type="text"
                                    value={generatedLink}
                                    readOnly
                                    className="share-link-input"
                                    onClick={() => linkRef.current?.select()}
                                />
                                <button className="share-copy-btn" onClick={handleCopy} disabled={!generatedLink}>
                                    {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy</>}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* QR Code */}
                    {qrDataUrl && (
                        <div className="share-qr">
                            <div className="share-qr-card">
                                <img src={qrDataUrl} alt="QR code for share link" draggable={false} />
                            </div>
                            <div className="share-qr-label">
                                <Smartphone size={13} />
                                <span>Scan to open on another device</span>
                            </div>
                        </div>
                    )}

                </div>

                <div className="share-footer">
                    <span className="share-footer-note">
                        This invite link contains encrypted credentials. The server never sees the encryption key.
                    </span>
                </div>
            </div>
        </>
    )
}
